import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import { Send, Save, Users, Loader2, ChevronRight, Search, X, CalendarClock } from "lucide-react";
import { Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import {
    WA_ACCOUNTS_URL, WA_TEMPLATES_URL, WA_CAMPAIGNS_URL, WA_CAMPAIGN_START_URL,
    WA_LEAD_FIELDS_URL, USERS_SIMPLE_URL
} from "../../constants/api";
import { LEAD_STAGES } from "../../constants/leadStages";
import ToastMsg from "../../components/common/ToastMsg";

const STEPS = ['Campaign Info', 'Audience', 'Template & Variables', 'Schedule', 'Review'];

const labelCls = 'text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block';

/* Sentinel for "not a lead field, just text". Not a possible field key, so it
   cannot collide with one the server adds later. */
const CUSTOM_SPEC = '__custom__';

export default function WhatsAppCampaignComposer() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { fetch, create, update } = useApiMethods();

    const [step, setStep] = useState(0);
    const [accounts, setAccounts] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [users, setUsers] = useState([]);
    const [leadFields, setLeadFields] = useState([]);
    const [audienceList, setAudienceList] = useState([]);
    const [matchedCount, setMatchedCount] = useState(null);
    const [listTruncated, setListTruncated] = useState(false);
    /* Searches the audience list only — never the filter. Typing a name here
       finds one lead among the matches to untick; it does not narrow who the
       campaign goes to, which is why it lives outside audience_filter and is
       never saved with the campaign. */
    const [search, setSearch] = useState('');
    // How many rows the search matched, so a capped list can say "first 500 of
    // 900 matching" instead of comparing itself to the unsearched audience.
    const [listTotal, setListTotal] = useState(0);
    /* Which variable positions the sender switched to "same text for everyone",
       as {"1": true}. The mapping alone cannot say: picking that option stores an
       empty string, which is indistinguishable from "nothing picked yet" — so the
       dropdown snapped straight back to "Pick lead data" and the text box never
       appeared, leaving no way to type the text at all. Screen state only; the
       wire format is unchanged, and a saved literal is recognised on load below. */
    const [customVars, setCustomVars] = useState({});
    const [unreachable, setUnreachable] = useState(0);
    const [audienceLoading, setAudienceLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [starting, setStarting] = useState(false);
    const [campaignId, setCampaignId] = useState(id || null);
    // Where the campaign stands on the server. Editing one that has already gone
    // out is refused by the API, so the screen says so up front instead of after
    // a wasted pass through five steps.
    const [campaignStatus, setCampaignStatus] = useState(null);

    const [form, setForm] = useState({
        name: '',
        account_id: null,
        template_id: null,
        template_variables_mapping: {},
        audience_filter: {
            lead_stage: [], city: '', owner_id: null, opt_in_only: false,
            from_date: '', to_date: '',
            // Default on: a landline, or a field holding the word "Mysuru", cannot
            // receive WhatsApp. The send path was already skipping those and
            // marking them failed, so showing them was the composer promising an
            // audience it could not reach.
            reachable_only: true,
            // Exclusions, not inclusions: everyone matching the filter is in by
            // default, and this holds only the ones the sender took out. So a
            // lead that starts matching after the draft was saved is still sent.
            excluded_ids: [],
        },
        schedule_at: '',
        throttle_per_minute: 80,
    });

    const loadData = useCallback(async () => {
        const [aRes, tRes, uRes, fRes] = await Promise.all([
            fetch(WA_ACCOUNTS_URL), fetch(WA_TEMPLATES_URL), fetch(USERS_SIMPLE_URL),
            fetch(WA_LEAD_FIELDS_URL),
        ]);
        if (fRes?.status === 'success') setLeadFields(fRes.data || []);
        if (aRes?.status === 'success') setAccounts(aRes.data || []);
        if (tRes?.status === 'success') setTemplates((tRes.data || []).filter(t => t.status === 'approved' && !t.is_disabled_in_crm));
        if (uRes?.status === 'success') {
            setUsers((uRes.data || []).map(u => ({
                id: u.id,
                label: `${u.login_fname || ''} ${u.login_lname || ''}`.trim() || u.login_name,
            })));
        }
    }, [fetch]);

    useEffect(() => { loadData(); }, [loadData]);

    /* /edit opened a blank composer: nothing ever read the campaign back, so the
       name, account, template, mapping, audience and schedule were all empty and
       saving wrote those blanks over a real campaign. USE_TZ is off server-side,
       so schedule_at comes back as plain local wall time ("2026-08-27T10:30:00")
       — the shape datetime-local wants, once the seconds are cut. */
    const loadCampaign = useCallback(async () => {
        if (!id) return;
        const res = await fetch(`${WA_CAMPAIGNS_URL}?id=${id}`);
        if (res?.status !== 'success' || !res.data) {
            ToastMsg('error', res?.message || 'Could not open this campaign');
            return;
        }
        const c = res.data;
        setCampaignStatus(c.status || null);
        setForm(prev => ({
            ...prev,
            name: c.name || '',
            account_id: c.account_id ?? null,
            template_id: c.template_id ?? null,
            template_variables_mapping: c.template_variables_mapping || {},
            // Spread over the defaults so a filter saved before a key existed
            // (reachable_only, excluded_ids) still arrives with one.
            audience_filter: { ...prev.audience_filter, ...(c.audience_filter || {}) },
            schedule_at: c.schedule_at ? String(c.schedule_at).slice(0, 16) : '',
            throttle_per_minute: c.throttle_per_minute ?? 80,
        }));
    }, [fetch, id]);

    useEffect(() => { loadCampaign(); }, [loadCampaign]);

    const fetchAudienceCount = useCallback(async (filter, q = '') => {
        setAudienceLoading(true);
        // The list is requested with the filter but WITHOUT the exclusions, so
        // unticking a row does not make it vanish from the list it was unticked
        // in — it just loses its tick, and can be put back.
        const { excluded_ids, ...forList } = filter || {};
        const params = new URLSearchParams({
            preview: '1', with_list: '1', filter: JSON.stringify(forList),
        });
        // Sent apart from the filter so the counts come back for the whole
        // audience while only the listed rows are narrowed.
        if (q) params.set('q', q);
        const res = await fetch(`${WA_CAMPAIGNS_URL}?${params}`);
        if (res?.status === 'success') {
            const d = res.data || {};
            setAudienceList(d.contacts || []);
            setMatchedCount(d.matched_count ?? d.audience_count ?? null);
            setListTruncated(!!d.truncated);
            setListTotal(d.list_count ?? (d.contacts || []).length);
            setUnreachable(d.unreachable_count ?? 0);
        }
        setAudienceLoading(false);
    }, [fetch]);

    /* Two jobs, one effect. Arriving on the Audience step loads the audience —
       it used to sit empty until a filter was touched, so the default "everyone"
       audience looked like nobody. And each keystroke in the search box refetches
       the list, debounced, because the rows being searched run past the 500 the
       screen holds: a client-side filter would report "not found" for a lead that
       is in the campaign. */
    useEffect(() => {
        if (step !== 1) return;
        const t = setTimeout(() => fetchAudienceCount(form.audience_filter, search),
                             search ? 350 : 0);
        return () => clearTimeout(t);
        // form.audience_filter is deliberately absent: filter edits refetch through
        // handleAudienceChange, which also clears the exclusions they invalidate.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, search]);

    const fc = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
    const fa = (field, value) => setForm(prev => ({
        ...prev,
        audience_filter: { ...prev.audience_filter, [field]: value },
    }));

    /* A mapping value is either a lead field ("owner_name"), a lead field with a
       fallback ("owner_name|our team"), or a literal sent to everyone. The three
       are one string on the wire, so these split and rebuild it rather than
       holding a second copy of the state that could drift from the form. */
    const rawSpec = (pos) => form.template_variables_mapping[pos] || '';
    const specHead = (pos) => rawSpec(pos).split('|')[0].trim();
    const specFallback = (pos) => rawSpec(pos).split('|').slice(1).join('|').trim();
    const isLeadField = (head) => leadFields.some(f => f.key === head);
    /* Nothing picked yet, a lead field, or literal text. The literal case is
       reached two ways: the sender chose "same text for everyone" this session
       (customVars), or the campaign was saved with a spec that is not a known
       field, which is what a literal looks like on the wire. Without the first,
       an empty literal read as nothing picked. */
    const specField = (pos) => {
        const head = specHead(pos);
        if (customVars[pos]) return CUSTOM_SPEC;
        if (!head) return undefined;
        return isLeadField(head) ? head : CUSTOM_SPEC;
    };
    // The whole spec, pipes included: a literal is sent as typed, so "Open 9|5"
    // is text and not a field with a fallback.
    const specCustom = (pos) => (isLeadField(specHead(pos)) ? '' : rawSpec(pos));

    const setMap = (pos, value) => fc('template_variables_mapping', {
        ...form.template_variables_mapping, [pos]: value,
    });
    const setMapField = (pos, v) => {
        const custom = v === CUSTOM_SPEC;
        setCustomVars(prev => ({ ...prev, [pos]: custom }));
        // Switching to "same text" keeps text already typed here, so glancing at a
        // lead field and coming back does not silently wipe it. Switching to a
        // field drops it, since it is about to be overwritten anyway.
        if (!custom) setMap(pos, v);
        else if (isLeadField(specHead(pos))) setMap(pos, '');
    };
    const setMapCustom = (pos, text) => setMap(pos, text);
    const setMapFallback = (pos, text) => {
        const head = specHead(pos);
        setMap(pos, text.trim() ? `${head}|${text}` : head);
    };

    const excludedIds = form.audience_filter.excluded_ids || [];
    const excludedCount = excludedIds.length;
    const isExcluded = (id) => excludedIds.includes(id);

    /* Derived, never stored. It was state set only inside the filter fetch, so
       unticking a row updated excluded_ids and left the headline number showing
       the pre-untick figure — the campaign sent the right people while the screen
       reported the wrong count. Every exclusion is guaranteed to be inside the
       current match set (see handleAudienceChange), so this subtraction is exact. */
    const audienceCount = matchedCount == null
        ? null
        : Math.max(0, matchedCount - excludedCount);

    /* Exclusions do not change who matches, so the list is never refetched here —
       only the tick and the send-count move. Refetching would reorder the rows
       under the cursor mid-review. */
    const setExcluded = (ids) => fa('excluded_ids', ids);
    const toggleContact = (id) => setExcluded(
        isExcluded(id) ? excludedIds.filter(x => x !== id) : [...excludedIds, id]
    );

    /* Both buttons act on the rows currently listed, not on the whole audience.
       With a search active "Select all" used to drop every exclusion — so
       searching for one lead and ticking it silently put back everyone the sender
       had already removed. Unlisted rows keep whatever tick they had. */
    const visibleIds = audienceList.map(c => c.id);
    const includeVisible = () => setExcluded(excludedIds.filter(id => !visibleIds.includes(id)));
    const excludeVisible = () => setExcluded([...new Set([...excludedIds, ...visibleIds])]);

    const handleAudienceChange = (field, value) => {
        /* Exclusions are dropped when the filter changes. They were picked from a
           different set of matches, so keeping them would silently remove people
           from a campaign nobody had reviewed them for — and would make the
           subtraction above wrong, since an excluded id need not be in the new
           match set at all. */
        const newFilter = { ...form.audience_filter, [field]: value, excluded_ids: [] };
        setForm(prev => ({ ...prev, audience_filter: newFilter }));
        fetchAudienceCount(newFilter, search);
    };

    // A schedule in the past is not refused — the beat task picks it up on its
    // next tick — but the sender is told, because "scheduled" reads as "later".
    const schedulePast = !!form.schedule_at && new Date(form.schedule_at) <= new Date();

    const selectedTemplate = templates.find(t => t.id === form.template_id);
    const varPositions = selectedTemplate
        ? [...new Set([...(selectedTemplate.body_text || '').matchAll(/\{\{(\d+)\}\}/g)].map(m => m[1]))]
        : [];

    /* Which required fields are missing, and which step each one lives on.
       The old check named all three whichever was blank and left the sender on
       whatever step they were on — so "Name, account, and template are required"
       arrived while looking at the audience filters, with nothing to say which
       one was actually wrong or where to go and fix it. */
    const missingRequired = () => [
        { step: 0, label: 'a campaign name', ok: !!(form.name || '').trim() },
        { step: 0, label: 'a WhatsApp account', ok: !!form.account_id },
        { step: 2, label: 'a template', ok: !!form.template_id },
        /* An unfilled variable is not a blank in the message — WhatsApp rejects a
           template parameter that is empty, so every recipient fails one at a
           time in the report. Caught here instead, while it can still be typed. */
        ...varPositions.map(pos => ({
            step: 2,
            label: `something to send for {{${pos}}}`,
            ok: !!rawSpec(pos).trim(),
        })),
    ].filter(f => !f.ok);

    /* Saves and hands back the id, which is what the callers actually need.
       Scheduling and starting used to call the draft save and then read
       `campaignId` — state from the render they were already in, so still null on
       a campaign being created. The first click therefore saved a draft and
       stopped dead, and nothing happened until the button was pressed a second
       time. Returning the id removes the round trip through state entirely. */
    const saveCampaign = async () => {
        const missing = missingRequired();
        if (missing.length) {
            const names = missing.map(m => m.label);
            const list = names.length === 1
                ? names[0]
                : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
            ToastMsg('error', `Still need ${list}`);
            // Land on the step that owns the first thing missing, so the fix is
            // in front of them rather than somewhere they have to go looking.
            setStep(missing[0].step);
            return null;
        }
        setSaving(true);
        const payload = {
            ...form,
            // Empty string is not a time. Sent as-is it is a validation error on
            // create and, on update, would clear a schedule the sender kept.
            schedule_at: form.schedule_at || null,
            template_variables_mapping: form.template_variables_mapping,
        };
        const res = campaignId
            ? await update(WA_CAMPAIGNS_URL, { id: campaignId, ...payload })
            : await create(WA_CAMPAIGNS_URL, payload);
        setSaving(false);
        if (res?.status !== 'success') {
            ToastMsg('error', res?.message || 'Save failed');
            return null;
        }
        const savedId = campaignId || res.data?.id || null;
        if (savedId && savedId !== campaignId) setCampaignId(savedId);
        return savedId;
    };

    const handleSaveDraft = async () => {
        const savedId = await saveCampaign();
        if (savedId) ToastMsg('success', campaignId ? 'Campaign updated' : 'Draft saved');
    };

    /* Scheduling does NOT touch the start endpoint. That endpoint sends now — so
       the button reading "Schedule Campaign" was firing the whole campaign the
       moment it was pressed, whatever time had been picked. Saving with a
       schedule_at is the whole job: the API puts the campaign in 'scheduled', and
       the beat task flips it to 'running' when the time actually arrives. */
    const handleSchedule = async () => {
        const savedId = await saveCampaign();
        if (!savedId) return;
        setCampaignStatus('scheduled');
        ToastMsg('success', `Scheduled for ${new Date(form.schedule_at).toLocaleString()}`);
        navigate('/whatsapp/campaigns');
    };

    const handleStart = async () => {
        const savedId = await saveCampaign();
        if (!savedId) return;
        setStarting(true);
        const res = await create(WA_CAMPAIGN_START_URL(savedId), {});
        setStarting(false);
        if (res?.status === 'success') {
            ToastMsg('success', `Campaign started — ${res.data?.total_recipients} recipients`);
            navigate('/whatsapp/campaigns');
        } else {
            ToastMsg('error', res?.message || 'Failed to start campaign');
        }
    };

    const renderStep = () => {
        switch (step) {
            case 0:
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Campaign Name *</label>
                            <Input value={form.name} onChange={e => fc('name', e.target.value)} placeholder="Summer Promo 2026" />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">WhatsApp Account *</label>
                            <Select
                                className="w-full"
                                placeholder="Select account"
                                value={form.account_id}
                                options={accounts.map(a => ({ value: a.id, label: `${a.display_name}${a.phone_number ? ` (${a.phone_number})` : ''}` }))}
                                onChange={v => fc('account_id', v)}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Messages per Minute</label>
                            <Input type="number" value={form.throttle_per_minute} onChange={e => fc('throttle_per_minute', parseInt(e.target.value) || 80)} placeholder="80" />
                        </div>
                    </div>
                );

            case 1:
                return (
                    <div className="space-y-4">
                        {/* One row. Five filters stacked over three rows pushed the
                            audience list below the fold, so choosing a stage and seeing
                            who it caught could not be done without scrolling. Wraps on a
                            narrow window rather than crushing the fields. */}
                        <div className="flex flex-wrap items-end gap-3">
                            <div className="flex-1 min-w-[210px]">
                                <label className={labelCls}>Lead Stage</label>
                                <Select
                                    mode="multiple"
                                    value={form.audience_filter.lead_stage || []}
                                    onChange={v => handleAudienceChange('lead_stage', v || [])}
                                    options={LEAD_STAGES.map(s => ({ value: s, label: s }))}
                                    placeholder="Any stage"
                                    className="w-full"
                                    allowClear
                                    maxTagCount="responsive"
                                />
                            </div>
                            <div className="flex-1 min-w-[170px]">
                                <label className={labelCls}>Owner</label>
                                {/* "All owners" is a real option, not just the empty state:
                                    clearing the box does the same thing, but nobody reads a
                                    placeholder as an instruction. '' maps to null below,
                                    which the backend treats as no filter. */}
                                <Select
                                    value={form.audience_filter.owner_id ?? ''}
                                    onChange={v => handleAudienceChange('owner_id', v || null)}
                                    options={[
                                        { value: '', label: 'All owners' },
                                        ...users.map(u => ({ value: u.id, label: u.label || u.name })),
                                    ]}
                                    className="w-full"
                                    showSearch
                                    filterOption={(input, opt) =>
                                        (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
                                />
                            </div>
                            <div className="flex-1 min-w-[130px]">
                                <label className={labelCls}>City</label>
                                <Input value={form.audience_filter.city}
                                    onChange={e => handleAudienceChange('city', e.target.value)}
                                    placeholder="Any city" />
                            </div>
                            {/* When the lead came in. Without it a campaign aimed at this
                                month's enquiries also reaches one from last November that
                                has already heard from us eleven times. */}
                            <div className="min-w-[145px]">
                                <label className={labelCls}>Created from</label>
                                <Input type="date" value={form.audience_filter.from_date || ''}
                                    onChange={e => handleAudienceChange('from_date', e.target.value)} />
                            </div>
                            <div className="min-w-[145px]">
                                <label className={labelCls}>Created to</label>
                                <Input type="date" value={form.audience_filter.to_date || ''}
                                    onChange={e => handleAudienceChange('to_date', e.target.value)} />
                            </div>
                            <label htmlFor="reachable_only"
                                title="Hide leads whose number cannot receive WhatsApp"
                                className="flex items-center gap-2 h-10 shrink-0 cursor-pointer
                                           text-sm text-gray-700 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    id="reachable_only"
                                    checked={form.audience_filter.reachable_only !== false}
                                    onChange={e => handleAudienceChange('reachable_only', e.target.checked)}
                                    className="rounded"
                                />
                                WhatsApp only
                            </label>
                            <label htmlFor="opt_in_only"
                                className="flex items-center gap-2 h-10 shrink-0 cursor-pointer
                                           text-sm text-gray-700 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    id="opt_in_only"
                                    checked={form.audience_filter.opt_in_only}
                                    onChange={e => handleAudienceChange('opt_in_only', e.target.checked)}
                                    className="rounded"
                                />
                                Opted-in only
                            </label>
                        </div>
                        {/* The audience itself, tickable. A bare count asked the sender
                            to trust a number they could not inspect; every row is listed,
                            everyone starts included, and unticking removes just that one.
                            Exclusions are stored, not inclusions, so the filter stays a
                            rule rather than a frozen snapshot. */}
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/10">
                                <div className="flex items-center gap-3 min-w-0">
                                    <Users className="h-5 w-5 text-blue-500 shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Will receive this campaign</p>
                                        {audienceLoading
                                            ? <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                                            : <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                                                  {audienceCount ?? '—'}
                                                  {excludedCount > 0 && (
                                                      <span className="text-xs font-normal text-gray-500 ml-2">
                                                          {matchedCount} match, {excludedCount} removed
                                                      </span>
                                                  )}
                                              </p>
                                        }
                                        {unreachable > 0 && (
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                                {unreachable} hidden — no usable WhatsApp number
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {/* Finding one lead in a four-thousand-lead audience was
                                        scrolling only, and the list is capped at 500 — so this
                                        searches on the server, across every match, not just the
                                        rows already down the wire. */}
                                    <div className="relative">
                                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5
                                                           text-gray-400 pointer-events-none" />
                                        <input
                                            type="text"
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                            placeholder="Search name or number"
                                            className="h-8 w-52 pl-7 pr-7 rounded-lg text-xs
                                                       bg-white dark:bg-gray-900
                                                       border border-gray-200 dark:border-gray-700
                                                       text-gray-800 dark:text-gray-100
                                                       placeholder:text-gray-400
                                                       focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        />
                                        {search && (
                                            <button type="button" onClick={() => setSearch('')}
                                                title="Clear search"
                                                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5
                                                           text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                    {audienceList.length > 0 && (
                                        <>
                                            {/* Named for what they do while a search is on: they tick
                                                and untick the rows in view and leave the rest of the
                                                audience as the sender left it. */}
                                            <button type="button" onClick={includeVisible}
                                                className="text-xs font-medium underline text-gray-600 dark:text-gray-300">
                                                {search ? 'Select these' : 'Select all'}
                                            </button>
                                            <span className="text-gray-300">|</span>
                                            <button type="button" onClick={excludeVisible}
                                                className="text-xs font-medium underline text-gray-600 dark:text-gray-300">
                                                {search ? 'Clear these' : 'Clear all'}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {audienceList.length === 0 ? (
                                <p className="px-4 py-6 text-xs text-center text-gray-500 dark:text-gray-400">
                                    {audienceLoading ? 'Working out who matches...'
                                        : search
                                            ? `No lead in this audience matches “${search}”. They may still be in the campaign — clear the search to see everyone.`
                                            : 'Nobody matches these filters yet. Widen the stage or clear the dates.'}
                                </p>
                            ) : (
                                <>
                                    <div className="max-h-[22rem] overflow-y-auto">
                                        {audienceList.map(c => {
                                            const on = !isExcluded(c.id);
                                            return (
                                                <label key={c.id}
                                                    className={cn(
                                                        "flex items-baseline gap-3 px-4 py-1.5 cursor-pointer text-sm",
                                                        "hover:bg-gray-50 dark:hover:bg-gray-800/40",
                                                        !on && "opacity-60",
                                                    )}>
                                                    <input type="checkbox" checked={on}
                                                        onChange={() => toggleContact(c.id)}
                                                        className="rounded shrink-0 self-center" />
                                                    <span className={cn("truncate",
                                                        on ? "text-gray-800 dark:text-gray-100"
                                                           : "text-gray-400 line-through")}>
                                                        {c.name || "(no name)"}
                                                    </span>
                                                    {/* Beside the name, not flung to the far edge — on a wide
                                                        screen a right-aligned column made the reader track
                                                        across empty space to pair the two. */}
                                                    <span className="text-xs tabular-nums text-gray-400 shrink-0">
                                                        {c.phone || 'no number'}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {/* Said out loud: a capped list shown as if it were the
                                        whole audience would have the sender tick 500 rows and
                                        believe they had reviewed 4,000. */}
                                    {listTruncated && (
                                        <p className="px-4 py-2 text-[11px] text-amber-700 dark:text-amber-400
                                                      bg-amber-50 dark:bg-amber-900/10">
                                            Showing the first {audienceList.length} of {listTotal}
                                            {search ? ' matching rows' : ''}. The rest are included and cannot be
                                            unticked here — {search ? 'search more precisely' : 'narrow the filters'}
                                            {' '}to review them.
                                        </p>
                                    )}
                                    {/* Without this a search that matched 3 of 900 looked like an
                                        audience of 3, one step away from Send. */}
                                    {search && !listTruncated && (
                                        <p className="px-4 py-2 text-[11px] text-gray-500 dark:text-gray-400
                                                      bg-gray-50 dark:bg-gray-800/40">
                                            {audienceList.length} of {matchedCount} shown — matching “{search}”.
                                            Everyone still ticked receives this campaign, searched or not.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Template *</label>
                            <Select
                                className="w-full"
                                placeholder="Select approved template"
                                value={form.template_id}
                                options={templates.map(t => ({
                                    value: t.id,
                                    label: `${t.template_name} (${t.language}) — ${t.category}`,
                                }))}
                                onChange={v => fc('template_id', v)}
                            />
                        </div>

                        {selectedTemplate && (
                            <>
                                <div className="bg-green-50 dark:bg-green-900/10 rounded-xl p-4 border border-green-200 dark:border-green-800">
                                    <p className="text-[10px] font-medium text-green-600 dark:text-green-400 mb-2">Template Preview</p>
                                    <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{selectedTemplate.body_text}</p>
                                    {selectedTemplate.footer_text && (
                                        <p className="text-xs text-gray-400 mt-2 border-t border-green-200 dark:border-green-800 pt-1">{selectedTemplate.footer_text}</p>
                                    )}
                                </div>

                                {varPositions.length > 0 && (
                                    <div className="space-y-2">
                                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Variable Mapping</p>
                                        {varPositions.map(pos => (
                                            <div key={pos} className="flex items-center gap-2">
                                                <span className="px-2 py-1 text-[11px] font-mono bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded w-12 text-center shrink-0">{`{{${pos}}}`}</span>
                                                {/* A picker of what a lead actually holds. It was a free-text
                                                    box hinting "customer_name, city, or literal value", but only
                                                    two of those resolved — mapping a variable to `city` sent
                                                    every customer the word "city". The list comes from the
                                                    server, which is what the sender resolves against, so a
                                                    field cannot appear here that the send path cannot fill. */}
                                                <Select
                                                    value={specField(pos)}
                                                    onChange={v => setMapField(pos, v)}
                                                    options={[
                                                        ...leadFields.map(f => ({ value: f.key, label: f.label })),
                                                        { value: CUSTOM_SPEC, label: 'Same text for everyone…' },
                                                    ]}
                                                    placeholder="Pick lead data"
                                                    className="flex-1 min-w-[170px]"
                                                    showSearch
                                                    filterOption={(input, opt) =>
                                                        (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
                                                />
                                                {specField(pos) === CUSTOM_SPEC ? (
                                                    <Input
                                                        placeholder="Text sent to everyone"
                                                        value={specCustom(pos)}
                                                        onChange={e => setMapCustom(pos, e.target.value)}
                                                        className="flex-1"
                                                        autoFocus
                                                    />
                                                ) : specField(pos) ? (
                                                    /* A lead with this field empty cannot be sent — WhatsApp
                                                       rejects a blank variable — so the fallback is offered
                                                       here rather than discovered in the failure log. */
                                                    <Input
                                                        placeholder="If empty, send this instead (optional)"
                                                        value={specFallback(pos)}
                                                        onChange={e => setMapFallback(pos, e.target.value)}
                                                        className="flex-1"
                                                    />
                                                ) : null}
                                            </div>
                                        ))}
                                        <p className="text-[10px] text-gray-400">
                                            Each variable is filled from the lead. Pick
                                            <span className="font-medium"> Same text for everyone</span> to send a
                                            fixed word instead — a rep's own name, for example.
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Leave blank to start immediately when you click "Start Campaign".
                        </p>
                        <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Schedule Date & Time (optional)</label>
                            <Input
                                type="datetime-local"
                                value={form.schedule_at}
                                onChange={e => fc('schedule_at', e.target.value)}
                            />
                        </div>
                        {/* What the schedule actually does, because "scheduled" and
                            "running" are the two words the list shows and nobody
                            should have to guess which one to expect tonight. */}
                        {form.schedule_at && (
                            <div className="rounded-lg bg-blue-50 dark:bg-blue-900/10 p-3 space-y-1">
                                <p className="text-xs text-blue-700 dark:text-blue-300">
                                    Saved as <span className="font-medium">scheduled</span>. Nothing is sent until
                                    {' '}{new Date(form.schedule_at).toLocaleString()} — it turns
                                    {' '}<span className="font-medium">running</span> then, and not before.
                                </p>
                                <p className="text-[11px] text-blue-600/80 dark:text-blue-300/70">
                                    Until it runs you can still edit or delete it from the campaign list.
                                </p>
                                {schedulePast && (
                                    <p className="text-[11px] text-amber-700 dark:text-amber-400">
                                        That time has already passed — it will go out at the next check, within a
                                        minute of saving.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-4">
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">Campaign</span>
                                <span className="font-medium text-gray-800 dark:text-gray-100">{form.name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">Template</span>
                                <span className="font-medium text-gray-800 dark:text-gray-100">{selectedTemplate?.template_name || '—'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">Audience</span>
                                <span className="font-medium text-gray-800 dark:text-gray-100">{audienceCount ?? '—'} contacts</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">Throttle</span>
                                <span className="font-medium text-gray-800 dark:text-gray-100">{form.throttle_per_minute} msg/min</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">Schedule</span>
                                <span className="font-medium text-gray-800 dark:text-gray-100">{form.schedule_at ? new Date(form.schedule_at).toLocaleString() : 'Immediately'}</span>
                            </div>
                        </div>
                        <p className="text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg p-3">
                            Once started, the campaign will be queued in Celery. Blacklisted and opted-out contacts will be skipped automatically.
                        </p>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="p-6">
            <ContainerCard>
                <div className="mb-6">
                    <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-800 dark:text-gray-100">
                        {id ? 'Edit Campaign' : 'New Campaign'}
                        {campaignStatus && (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium
                                             bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                {campaignStatus}
                            </span>
                        )}
                    </h2>
                    {/* The API refuses to update these, so say it here rather than
                        letting five steps of edits end in "Cannot edit a running
                        campaign". */}
                    {['running', 'completed'].includes(campaignStatus) && (
                        <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                            This campaign has already {campaignStatus === 'running' ? 'started' : 'finished'} —
                            it can no longer be changed.
                        </p>
                    )}

                    {/* Step indicator */}
                    <div className="flex items-center gap-1 mt-4 overflow-x-auto pb-1">
                        {STEPS.map((s, i) => (
                            <div key={s} className="flex items-center gap-1">
                                <button
                                    onClick={() => setStep(i)}
                                    className={cn(
                                        'px-3 py-1 rounded-full text-xs font-medium transition-colors',
                                        step === i
                                            ? 'bg-green-500 text-white'
                                            : i < step
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                                    )}
                                >
                                    {i + 1}. {s}
                                </button>
                                {i < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-gray-400" />}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Steps are capped in width. Full-bleed on a wide monitor put a
                    date field a thousand pixels from its own label and left the
                    contact name and its phone number at opposite ends of the row. */}
                <div className="min-h-[200px] max-w-6xl pb-4">
                    {renderStep()}
                </div>

                {/* Sticky, opaque, and above the content. ContainerCard is a fixed-height
                    scroller, so a footer in normal flow was being scrolled over by the
                    audience list — the buttons sat on top of the rows and hid one of them
                    entirely. mt-auto pins it to the bottom of the flex column when the
                    step is short. */}
                <div className="sticky bottom-0 z-10 mt-auto flex items-center justify-between gap-3
                                border-t border-gray-200 dark:border-gray-700
                                bg-background px-1 py-3">
                    <div className="flex gap-2">
                        {step > 0 && (
                            <Button variant="outline" onClick={() => setStep(s => s - 1)} className="text-sm">
                                Back
                            </Button>
                        )}
                        {step < STEPS.length - 1 && (
                            <Button onClick={() => setStep(s => s + 1)} className="text-sm">
                                Next
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={handleSaveDraft}
                            disabled={saving}
                            className="flex items-center gap-2 text-sm"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {/* An existing scheduled campaign is not demoted by saving it,
                                so calling the button "Save Draft" there was a lie. */}
                            {campaignId ? 'Save Changes' : 'Save Draft'}
                        </Button>
                        {/* Offered on the Schedule step too, once a time is set: the
                            first save of a new campaign had to be a draft, then a
                            second trip to Review to schedule it. */}
                        {(step === STEPS.length - 1 || (step === 3 && !!form.schedule_at)) && (
                            form.schedule_at ? (
                                <Button
                                    onClick={handleSchedule}
                                    disabled={saving || starting}
                                    className="flex items-center gap-2 text-sm bg-primary hover:bg-primary/90"
                                >
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                                    Schedule Campaign
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleStart}
                                    disabled={starting || saving}
                                    className="flex items-center gap-2 text-sm bg-green-500 hover:bg-green-600"
                                >
                                    {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                    Start Campaign
                                </Button>
                            )
                        )}
                    </div>
                </div>
            </ContainerCard>
        </div>
    );
}
